import React, { useState } from 'react';
import CatalogSelector from '../components/CatalogSelector';
import EnhancedFeedbackWidget from '../components/EnhancedFeedbackWidget';
import ValidationAssistant from '../components/ValidationAssistant';

const AnalyzePage = () => {
  const [files, setFiles] = useState([]);
  const [selectedMode, setSelectedMode] = useState('tolerant');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState([]);

  // États pour feedback enrichi
  const [feedbackWidget, setFeedbackWidget] = useState(null); // null | item
  const [validationAssistant, setValidationAssistant] = useState(null); // null | item
  const [modalOpen, setModalOpen] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);

  const handleFeedback = (item) => {
    // Ouvrir le widget enrichi avec l'item complet
    setFeedbackWidget(item);
  };

  const handleFeedbackSubmit = (feedbackData) => {
    // Feedback envoyé avec succès - fermer le widget
    setFeedbackWidget(null);
    alert('✅ Feedback enregistré pour améliorer le LLM !');
  };

  const handleShowValidationAssistant = (item) => {
    // Ouvrir mode validation assistée
    setValidationAssistant(item);
  };

  const handleValidationSubmit = (feedbackData) => {
    // Validation assistée complétée
    setValidationAssistant(null);
    alert('✅ Validation assistée enregistrée !');
  };

  const submitSimpleFeedback = async (itemId, feedbackType, equipmentText, matchRef, extraData = {}) => {
    try {
      const response = await fetch('https://api.aimycore.fr/api/analyze/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_id: itemId,
          feedback_type: feedbackType,
          equipment_text: equipmentText,
          match_ref: matchRef || '',
          timestamp: new Date().toISOString(),
          user_input: extraData
        })
      });
      
      if (response.ok) {
        alert(`✅ Feedback "${feedbackType}" enregistré pour l'entraînement du LLM !`);
      } else {
        alert('Erreur lors de l\'envoi du feedback');
      }
    } catch (error) {
      alert('Feedback enregistré localement');
    }
  };

  const handleCatalogSelection = async (selectedItems) => {
    if (!currentFeedback || selectedItems.length === 0) return;
    
    const alternatives = selectedItems.map(item => `${item.ref} - ${item.label}`).join('; ');
    
    await submitSimpleFeedback(
      currentFeedback.itemId, 
      'suggestions', 
      currentFeedback.equipmentText,
      currentFeedback.matchRef,
      { suggested_alternatives: alternatives, selected_count: selectedItems.length }
    );
    
    alert(`✅ ${selectedItems.length} alternatives suggérées pour l'entraînement du LLM !`);
    setCurrentFeedback(null);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).map(file => ({
      id: `file_${Date.now()}_${Math.random()}`,
      file,
      customName: file.name.replace('.pdf', '')
    }));
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files)
      .filter(file => file.type === 'application/pdf')
      .map(file => ({
        id: `file_${Date.now()}_${Math.random()}`,
        file,
        customName: file.name.replace('.pdf', '')
      }));
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const updateFileName = (id, newName) => {
    setFiles(prev => prev.map(f => 
      f.id === id ? { ...f, customName: newName } : f
    ));
  };

  const analyzeFiles = async () => {
    if (files.length === 0) {
      alert('Veuillez sélectionner au moins un fichier PDF');
      return;
    }

    setIsAnalyzing(true);
    const newResults = [];

    for (const fileItem of files) {
      const formData = new FormData();
      formData.append('show_file', fileItem.file);
      const modeToSend = selectedMode === 'strict' ? 'strict' : 'tolerant';
      const analyzeUrl = new URL('https://api.aimycore.fr/api/analyze/');
      analyzeUrl.searchParams.set('mode', modeToSend);

      try {
        const response = await fetch(analyzeUrl.toString(), {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          newResults.push({
            id: fileItem.id,
            filename: fileItem.customName,
            originalFile: fileItem.file.name,
            result,
            mode: modeToSend,
            cache_hit: result.cache_hit || false
          });
        } else {
          const errorText = await response.text();
          newResults.push({
            id: fileItem.id,
            filename: fileItem.customName,
            error: `Erreur ${response.status}: ${errorText}`
          });
        }
      } catch (error) {
        newResults.push({
          id: fileItem.id,
          filename: fileItem.customName,
          error: `Erreur réseau: ${error.message}`
        });
      }
    }

    setResults(newResults);
    setIsAnalyzing(false);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: '#2563eb', fontSize: '2rem', marginBottom: '8px' }}>🎯 Service ANALYZE</h1>
        <p style={{ color: '#6b7280', fontSize: '1.1rem' }}>
          Analyse automatique de devis techniques pour le spectacle vivant
        </p>
        <p><a href="/simu-remuneration" style={{ color: '#2563eb' }}>← Retour au simulateur</a></p>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>📁 Upload de fichiers</h2>
        
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          style={{
            border: '2px dashed #d1d5db',
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
            marginBottom: '20px'
          }}
        >
          <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '12px' }}>
            Glissez-déposez vos fichiers PDF ici ou
          </p>
          <input
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="fileInput"
          />
          <label
            htmlFor="fileInput"
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Choisir des fichiers
          </label>
        </div>

        {files.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1rem', marginBottom: '12px', color: '#374151' }}>
              Fichiers sélectionnés ({files.length})
            </h3>
            {files.map(fileItem => (
              <div key={fileItem.id} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                backgroundColor: '#f3f4f6',
                borderRadius: '6px',
                marginBottom: '8px'
              }}>
                <input
                  type="text"
                  value={fileItem.customName}
                  onChange={(e) => updateFileName(fileItem.id, e.target.value)}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    marginRight: '12px'
                  }}
                />
                <span style={{ fontSize: '0.8rem', color: '#6b7280', marginRight: '12px' }}>
                  ({fileItem.file.name})
                </span>
                <button
                  onClick={() => removeFile(fileItem.id)}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  Supprimer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>⚙️ Mode d'analyse</h2>
        <div style={{ display: 'flex', gap: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="mode"
              value="tolerant"
              checked={selectedMode === 'tolerant'}
              onChange={(e) => setSelectedMode(e.target.value === 'strict' ? 'strict' : 'tolerant')}
              style={{ marginRight: '8px' }}
            />
            <span>
              <strong>Tolerant</strong> (score ≥ 0.3) - Inclut les équivalents fonctionnels
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="mode"
              value="strict"
              checked={selectedMode === 'strict'}
              onChange={(e) => setSelectedMode(e.target.value === 'strict' ? 'strict' : 'tolerant')}
              style={{ marginRight: '8px' }}
            />
            <span>
              <strong>Strict</strong> (score ≥ 0.7) - Matches exacts uniquement
            </span>
          </label>
        </div>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={analyzeFiles}
          disabled={files.length === 0 || isAnalyzing}
          style={{
            padding: '12px 24px',
            backgroundColor: files.length === 0 || isAnalyzing ? '#9ca3af' : '#059669',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1.1rem',
            cursor: files.length === 0 || isAnalyzing ? 'not-allowed' : 'pointer'
          }}
        >
          {isAnalyzing ? '🔄 Analyse en cours...' : '🚀 Analyser'}
        </button>
      </div>

      {results.length > 0 && (
        <div>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#374151' }}>📊 Résultats</h2>
          {results.map(result => (
            <div key={result.id} style={{
              marginBottom: '24px',
              padding: '20px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, color: '#1f2937' }}>{result.filename}</h3>
                {result.cache_hit && (
                  <span style={{
                    marginLeft: '12px',
                    padding: '2px 8px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    fontSize: '0.7rem',
                    borderRadius: '4px'
                  }}>
                    CACHE
                  </span>
                )}
              </div>

              {result.error ? (
                <p style={{ color: '#dc2626' }}>❌ {result.error}</p>
              ) : (
                <div>
                  <p style={{ marginBottom: '8px' }}>
                    <strong>Mode:</strong> {result.mode} | 
                    <strong> Équipements extraits:</strong> {result.result.items?.length || 0} | 
                    <strong> Matches trouvés:</strong> {result.result.ui_items?.length || 0}
                  </p>
                  
                  {result.result.ui_items?.length > 0 && (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {result.result.ui_items.map((item, idx) => (
                        <div key={idx} style={{
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          marginBottom: '8px',
                          fontSize: '0.9rem'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <strong>{item.label}</strong> → {item.match_label || 'Pas de match'}
                              {item.match_score && (
                                <span style={{ color: '#059669', marginLeft: '8px' }}>
                                  (Score: {(item.match_score * 100).toFixed(1)}%)
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleFeedback(item)}
                                style={{ padding: '6px 12px', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}
                                title="Ouvrir feedback enrichi"
                              >
                                💬 Validation
                              </button>
                              <button
                                onClick={() => handleShowValidationAssistant(item)}
                                style={{ padding: '6px 12px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}
                                title="Mode validation assistée rapide"
                              >
                                🚀 Rapide
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {feedbackWidget && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <EnhancedFeedbackWidget
            item={feedbackWidget}
            onSubmit={handleFeedbackSubmit}
            onCancel={() => setFeedbackWidget(null)}
          />
        </div>
      )}

      {validationAssistant && (
        <ValidationAssistant
          item={validationAssistant}
          onSubmit={handleValidationSubmit}
          onCancel={() => setValidationAssistant(null)}
        />
      )}

      <CatalogSelector
        isOpen={modalOpen}
        onClose={() => {setModalOpen(false); setCurrentFeedback(null);}}
        onSelect={handleCatalogSelection}
        equipmentText={currentFeedback?.equipmentText || ''}
      />
    </div>
  );
};

export default AnalyzePage;
